/* eslint-disable camelcase */
import axios from 'axios';
import { get } from 'lodash';
import PropTypes from 'prop-types';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Row, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import SlidingPane from "react-sliding-pane";
import ProgressBar from '../../../../shared/components/ProgressBar/ProgressBar';
import useBreakpoint, { SIZE_XS } from '../../../../shared/hooks/useBreakpoint';
import InstagramError from './InstagramAuth/Error';
import OptionalInstagramOAuth from './InstagramAuth/Optional_OAuth';
import RequiredInstagramOAuth from './InstagramAuth/Required_OAuth';
import InstagramSkipConnection from './InstagramAuth/SkipConnection';
import InstagramSuccess from './InstagramAuth/Success';
import RequiredInformation, { hasAnsweredAllQuestions, userOfferSignUpObjectAttribute, userObjectAttribute } from './RequiredInformation';
import SelectReward, { rewardHasOptionsToRedeem } from './SelectReward';
import TermsAndLegal, { hasFilledTermsAndLegals } from './TermsAndLegal';
import YouTubeAuth from './YoutubeAuth/YouTube__Auth';
import YouTubeError from './YoutubeAuth/YouTube__Error';
import YouTubeSuccess from './YoutubeAuth/YouTube__Success';

const STEPS_COMPONENTS = {
  TermsAndLegal,
  RequiredInformation,
  SelectReward,
  YouTubeAuth,
  YouTubeSuccess,
  YouTubeError,
  RequiredInstagramOAuth,
  OptionalInstagramOAuth,
  InstagramSuccess,
  InstagramError,
  InstagramSkipConnection
};


const offerHasRewards = (rewards) => rewards.some(r => rewardHasOptionsToRedeem(r));

const offerHasRequiredInfo = (offer) => {
  const rewards = get(offer, 'rewards') || [];
  return Boolean(get(offer, 'questions.length') ||
    get(offer, 'object_attribute_metadata', [])
      .filter(oa => userOfferSignUpObjectAttribute(oa) || userObjectAttribute(oa)) ||
    rewards.some(r => r.payment_email_required || r.phone_number_required || r.address_required) ||
    offer.facebook_oauth_required);
};

/**
 *
 * @param {{
 * open: boolean
 * onRequestClose: (extra = { }) => void
 * onAfterClose: onAfterClose
 * }} params
 * @returns
 */
const ApplySidePane = ({
  open,
  onRequestClose,
  onAfterClose,
}) => {
  const { t } = useTranslation();
  const user = useSelector(store => get(store, 'auth.user'));
  const isInfluencerWithOfferAccessToken = useSelector(store => get(store, 'auth.isInfluencerWithOfferAccessToken'));
  const offer = useSelector(store => get(store, 'offer.offer'));
  const userSignedUp = useSelector(store => get(store, 'offer.userSignedUp'));
  const breakpoint = useBreakpoint();
  const oauthSettings = useSelector(store => get(store, 'offer.oAuthSettings'));
  const testFlow = useSelector(store => get(store, 'config.test'));

  const facebookOAuthRequired = testFlow.includes('fb_required')
    ? true
    : oauthSettings.facebook.required || false;
  const facebookOauthOptional = testFlow.includes('fb_optional')
    ? true
    : oauthSettings.facebook.optional || false;
  const youtubeOAuthRequired = testFlow.includes('yt')
    ? true
    : oauthSettings.youtube.required || false;
  const fbOauthResponse = oauthSettings.facebook.response;
  const ytOauthResponse = oauthSettings.youtube.response;

  const [state, setState] = useState({
    currentStep: 1,
    totalSteps: 1,
    steps: [],
    userOffer: null,
    applied: false,
    termsAndLegalComplete: false,
    hasRequiredInfo: false,
    hasRewards: false,
    needToConnectFb: facebookOAuthRequired || facebookOauthOptional,
    isOauthProcess: false,
    loading: true,
    userOfferTmp: null,
  });

  useEffect(() => {
    let newState = { ...state };

    axios.get(`/api/v1/influencers/offers/${offer.id}/user_offers`)
      .then(({ data: { user_offer: userOffer } }) => {
        newState = {
          ...newState,
          userOffer,
        };
      }).catch(() => {
      }).finally(() => setupStepsProcess(newState));
  }, []);

  const setupYoutubeOAuth = useCallback((userOffer = {}, currentSteps = [], connected = false) => {
    if (!youtubeOAuthRequired) return currentSteps;

    if (connected && oauthSettings.youtube.response === 'success') {
      currentSteps.push('YouTubeSuccess');
    } else if (!connected) {
      if (get(userOffer, 'yout_hash_error') || oauthSettings.youtube.response === 'error') {
        currentSteps.push('YouTubeError');
      } else {
        currentSteps.push('YouTubeAuth');
      }
    }
    return [...currentSteps];
  }, []);


  const setupFacebookOAuth = useCallback((
    currentSteps = [],
    connected = false
  ) => {
    if (!facebookOAuthRequired && !facebookOauthOptional) return currentSteps;

    /* If there's a response from the OAuth callback we add success/errors steps */
    if (connected && fbOauthResponse === 'success') {
      facebookOAuthRequired
        ? currentSteps.unshift('InstagramSuccess')
        : currentSteps.push('InstagramSuccess');
    } else if (!connected) {
      /* If user has not IG connected  */
      if (fbOauthResponse === 'error') {
        facebookOAuthRequired
          ? currentSteps.unshift('InstagramError')
          : currentSteps.push('InstagramError');
      } else if (facebookOAuthRequired) {
        currentSteps.unshift('RequiredInstagramOAuth');
      }
    }
    return [...currentSteps];
  }, []);

  const setupStepsProcess = useCallback((newState) => {
    let steps = [];
    const termsAndLegalIncomplete = !hasFilledTermsAndLegals(offer);

    let facebookConnected = get(user, 'facebook_oauth_token');
    if (testFlow.includes('fb_success')) {
      facebookConnected = true;
    } else if (testFlow.some(t => /fb_(required|optional)/.test(t))) {
      facebookConnected = false;
    }

    let youtubeConnected = get(newState, 'userOffer.yout_oauth_token')
      && get(newState, 'userOffer.yout_oauth_provide') === 'youtube_publication';
    if (testFlow.includes('yt_success')) {
      youtubeConnected = true;
    } else if (testFlow.includes('yt') || testFlow.includes('yt_error')) {
      youtubeConnected = false;
    }

    // Initial step
    let currentStep = 'TermsAndLegal';
    let currentStepIndex = 1;
    steps.push(currentStep);

    const hasRequiredInfo = offerHasRequiredInfo(offer);
    const hasRewards = offerHasRewards(get(offer, 'rewards') || []);

    if (hasRequiredInfo) steps.push('RequiredInformation');
    if (hasRewards) steps.push('SelectReward');

    steps = setupFacebookOAuth(steps, facebookConnected);
    steps = setupYoutubeOAuth(newState.userOffer, steps, youtubeConnected);

    // Check where in the process the user is if has not finished
    // If already filled required information
    if (!userSignedUp || !termsAndLegalIncomplete) {
      // If has not required information or already filled goes to SelectReward component
      const requiredInformationDone = !hasRequiredInfo || hasAnsweredAllQuestions(offer.user_offer, offer);

      if (requiredInformationDone && hasRewards) { // If has completed required information and has rewards
        currentStep = 'SelectReward';
      } else if (hasRequiredInfo && !termsAndLegalIncomplete) { // If has required information and has not filled terms and legal
        currentStep = 'RequiredInformation';
      } else if (!facebookConnected && fbOauthResponse === 'error') {
        currentStep = 'InstagramError';
      } else if (!facebookConnected && facebookOAuthRequired) { // If has not connected Instagram and is required
        currentStep = 'RequiredInstagramOAuth';
      } else if (facebookConnected && fbOauthResponse === 'success') { // If has recently connected Instagram
        currentStep = 'InstagramSuccess';
      } else if (youtubeConnected && ytOauthResponse) { // If has recently connected YouTube
        currentStep = ytOauthResponse === 'success' ? 'YouTubeSuccess' : 'YouTubeError';
      }
      currentStepIndex = steps.indexOf(currentStep) + 1;
    }

    newState = {
      ...newState,
      currentStep: currentStepIndex,
      totalSteps: steps.length,
      steps,
      termsAndLegalComplete: !termsAndLegalIncomplete,
      hasRequiredInfo,
      hasRewards,
      loading: false,
    };
    setState({ ...newState });
  }, []);

  const changeStep = useCallback((step, newState) => {
    // If is not the last step move to next and scroll to top
    if (step > state.totalSteps) {
      // If has reached last step, checks if has to setup OAuth (YouTube or Instagram)
      // else calls function to sign up and close the modal
      if (
        (
          (get(newState, 'connectFacebook') === true && facebookOauthOptional)
        ) && !state.isOauthProcess
      ) {
        return setupOAuth(newState);
      }
      return onEndSignUp(newState);
    }

    // If has not reached final step go to next and up to top
    document.querySelector('.slide-pane__content').scrollTo({ top: 0, behavior: 'smooth' });

    setState({
      ...state,
      ...(!!newState && newState),
      currentStep: step,
    });

  }, [state]);

  const nextStep = useCallback((newState) => changeStep(state.currentStep + 1, newState), [changeStep, state]);
  const prevStep = useCallback(() => changeStep(state.currentStep - 1), [changeStep, state]);
  const lastStep = useCallback(() => state.currentStep == state.totalSteps);

  const setupOAuth = useCallback((newState) => {
    if (facebookOauthOptional || facebookOAuthRequired) {
      setState({
        ...state,
        currentStep: 1,
        totalSteps: 2,
        steps: ['OptionalInstagramOAuth', 'InstagramSkipConnection'],
        isOauthProcess: true,
        needToConnectFb: true,
        userOfferTmp: newState.userOfferTmp,
      });
    }
  }, [state]);

  const onEndSignUp = useCallback((newState) => {
    setState({
      ...state,
      applied: true
    });

    if ((!state.hasRequiredInfo && !state.hasRewards) || get(newState, 'connectFacebook')) {
      let userOffer;
      if (get(newState, 'connectFacebook')) {
        userOffer = {
          ...state.userOfferTmp,
        };
        delete userOffer.skip_validation;
      } else {
        userOffer = get(newState, 'offer.user_offer', state.userOffer);
      }

      axios.patch(`/api/v1/influencers/user_offers/${userOffer.id}`, { user_offer: userOffer })
        .then(() => onRequestClose())
        .catch(e => console.log(e));
      return;
    }
    onRequestClose();
  }, [state]);

  const CurrentComponent = useMemo(() => state.steps.length ?
    STEPS_COMPONENTS[state.steps[state.currentStep - 1]] :
    () => null
    , [state.steps, state.currentStep]);

  return (
    <SlidingPane
      overlayClassName="slide-over"
      isOpen={open}
      closeIcon={null}
      width={breakpoint === SIZE_XS ? "100%" : "450px"}
      shouldCloseOnEsc
      onRequestClose={onRequestClose}
      onAfterClose={() => onAfterClose(state.applied)}
      hideHeader
      from={document.body.dir === 'ltr' ? 'right' : 'left'}
    >
      <div>
        <ProgressBar now={Math.floor(state.currentStep * 100 / state.totalSteps)} className="mb-5" />

        <div className="text-medium-3">
          {t('influencers.common.step_of', {
            now: state.currentStep,
            end: state.totalSteps,
          })}
        </div>

        {state.loading
          ? <Row className="justify-content-center mt-4">
            <Spinner animation="border" />
          </Row>
          : <CurrentComponent
            termsAndLegalComplete={state.termsAndLegalComplete}
            userOffer={state.userOffer}
            onRequestClose={onRequestClose}
            nextStep={nextStep}
            prevStep={prevStep}
            lastStep={lastStep}
            facebookIsOptional={facebookOauthOptional}
          />
        }

      </div>
    </SlidingPane>
  );
};

ApplySidePane.propTypes = {
  open: PropTypes.bool,
  onRequestClose: PropTypes.func.isRequired,
  onAfterClose: PropTypes.func,
};

ApplySidePane.defaultProps = {
  open: false,
  onAfterClose: () => { },
};

export default ApplySidePane;
